import React, { useEffect } from 'react'

type ImgSanityProps = {
  src: string
  alt: string
  width?: number
  maxWidth?: number
  height?: number
  maxHeight?: number
  quality?: number
  priority?: boolean
}

function getDevicePixelRatio() {
  if (typeof window === 'undefined') {
    return 1
  }
  return window.devicePixelRatio
}

export function ImgSanity(
  props: ImgSanityProps & React.ImgHTMLAttributes<HTMLImageElement>
) {
  const { src, alt, maxWidth, maxHeight, priority, ...rest } = props
  // Device Pixel Ratio
  const [dpr, setDpr] = React.useState<number>(1)

  const url = new URL(props.src || 'http://void.com/')
  url.searchParams.set('auto', 'format')
  url.searchParams.set('q', String(props.quality || 75))

  if (dpr) {
    url.searchParams.set('dpr', String(Math.min(dpr, 2))) // Cap at 2x for performance
  }
  if (props.height) {
    url.searchParams.set('h', String(props.height))
  }
  if (props.width) {
    url.searchParams.set('w', String(props.width))
  }
  if (props.maxWidth) {
    url.searchParams.set('max-w', String(props.maxWidth))
  }
  if (props.maxHeight) {
    url.searchParams.set('max-h', String(props.maxHeight))
  }

  useEffect(() => {
    setDpr(getDevicePixelRatio())
    const updateDpr = () => {
      setDpr(getDevicePixelRatio())
    }

    // Listen for changes in device pixel ratio
    window
      .matchMedia(`(resolution: ${getDevicePixelRatio()}dppx)`)
      .addEventListener('change', updateDpr)

    // Cleanup listener on unmount
    return () => {
      window
        .matchMedia(`(resolution: ${getDevicePixelRatio()}dppx)`)
        .removeEventListener('change', updateDpr)
    }
  }, [])

  // WARNIGN - Image in absolute positioning wont render in safari with the properties:
  // loading="lazy"
  // Use regular img instead of this component.
  return (
    <img
      {...rest}
      style={{
        ...rest.style,
        maxWidth: maxWidth ?? rest.style?.maxWidth,
        maxHeight: maxHeight ?? rest.style?.maxHeight,
      }}
      src={url.toString()}
      alt={props.alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      fetchPriority={priority ? 'high' : undefined}
    />
  )
}
